import React, { useMemo, useState } from 'react';
import {
  Alert, Box, Container, Grid, Paper, Tab, Tabs, Typography, MenuItem, Select, FormControl,
  InputLabel, Table, TableBody, TableCell, TableHead, TableRow, Chip, Skeleton,
} from '@mui/material';
import {
  Download, Paid, Public, TrendingUp, Podcasts, AdsClick, Inbox,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from 'recharts';
import { flightpathAPI, megaphoneMumAPI } from '../services/api';
import { useUrlIntState } from '../hooks/useUrlState';
import SectionTitle from './crt/SectionTitle';
import Panel from './crt/Panel';

const fmt = (n: number | null | undefined) => (n ?? 0).toLocaleString();
const usd = (n: number | null | undefined) =>
  (n ?? 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });

const pctChip = (p: number | null | undefined) => {
  if (p === null || p === undefined) return <Chip size="small" label="—" sx={{ opacity: 0.5 }} />;
  const up = p >= 0;
  return (
    <Chip
      size="small"
      label={`${up ? '▲' : '▼'} ${Math.abs(p).toFixed(1)}%`}
      sx={{
        color: up ? '#00ff41' : '#ff3344',
        borderColor: up ? '#00ff41' : '#ff3344',
        border: '1px solid',
        background: 'transparent',
        fontFamily: "'VT323', monospace",
      }}
    />
  );
};

interface TabPanelProps { children?: React.ReactNode; index: number; value: number; }
const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
  </div>
);

const DAY_OPTIONS = [7, 30, 90, 180, 365];

interface FlightpathDashboardProps {
  /** When true, render without the outer Container / SectionTitle so it
   *  can be embedded inside another tabbed dashboard. */
  embedded?: boolean;
}

const FlightpathDashboard: React.FC<FlightpathDashboardProps> = ({ embedded = false }) => {
  const [active, setActive] = useUrlIntState('fp_tab', 0);
  const [days, setDays] = useState<number>(90);
  const [show, setShow] = useState<string>('');

  const showParam = show === '' ? undefined : show;

  const { data: shows, isLoading: showsLoading } = useQuery({
    queryKey: ['fp-shows'],
    queryFn: flightpathAPI.getShows,
    staleTime: 5 * 60 * 1000,
  });
  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ['fp-summary', days, showParam],
    queryFn: () => flightpathAPI.getSummary(days, showParam),
    staleTime: 60000,
  });
  const { data: timeline } = useQuery({
    queryKey: ['fp-timeline', days, showParam],
    queryFn: () => flightpathAPI.getTimeline(days, showParam),
    staleTime: 60000,
  });
  const { data: top } = useQuery({
    queryKey: ['fp-top-eps', days, showParam],
    queryFn: () => flightpathAPI.getTopEpisodes(days, 25, showParam),
    staleTime: 60000,
  });
  const { data: geo } = useQuery({
    queryKey: ['fp-geo', days, showParam],
    queryFn: () => flightpathAPI.getByCountry(days, 25, showParam),
    staleTime: 60000,
  });
  const { data: slots } = useQuery({
    queryKey: ['fp-slots', days, showParam],
    queryFn: () => flightpathAPI.getBySlot(days, showParam),
    staleTime: 60000,
  });
  const { data: ingest } = useQuery({
    queryKey: ['fp-ingest'],
    queryFn: flightpathAPI.getIngestStatus,
    staleTime: 60000,
  });

  // Mall Brats fallback: Flightpath has zero MB data (SES emails only
  // cover the SL network), so when the user picks MB we transparently
  // swap the timeline + top-episodes sources to MUM. Other dimensions
  // (country, DAI slot, ad revenue) aren't available from MUM — those
  // panels get a "not available for MB" banner instead.
  const isMbFallback = show === 'Mall Brats';
  const { data: mumTimeline } = useQuery({
    queryKey: ['fp-fb-mum-timeline', days],
    queryFn: () => megaphoneMumAPI.timeline('mallbrats', days),
    enabled: isMbFallback,
    staleTime: 60000,
  });
  const { data: mumTop } = useQuery({
    queryKey: ['fp-fb-mum-top', days],
    queryFn: () => megaphoneMumAPI.topEpisodes({
      site: 'mallbrats', days, limit: 25,
    }),
    enabled: isMbFallback,
    staleTime: 60000,
  });

  const timelinePoints = useMemo(
    () => {
      if (isMbFallback) {
        return (mumTimeline?.timeline || []).map((p: any) => ({
          date: p.metric_date,
          downloads: Number(p.downloads || 0),
          ad_revenue: 0,  // MUM doesn't have revenue — column stays zero, shown UI-side
        }));
      }
      return (timeline?.points || []).map((p: any) => ({
        date: p.date,
        downloads: p.downloads,
        ad_revenue: Number(p.ad_revenue || 0),
      }));
    },
    [timeline, mumTimeline, isMbFallback],
  );

  const slotPoints = useMemo(
    () => (slots?.slots || []).map((s: any) => ({
      slot: s.slot,
      downloads: s.downloads,
      ad_revenue: Number(s.ad_revenue || 0),
    })),
    [slots]
  );

  const body = (
    <>
      <Box mb={4} display="flex" alignItems="flex-start" justifyContent={embedded ? 'flex-end' : 'space-between'} flexWrap="wrap" gap={2}>
        {!embedded && (
          <SectionTitle sub="&gt; Flightpath S3 delivery — downloads, impressions, ad revenue_">
            PODCAST :: DOWNLOADS CONSOLE
          </SectionTitle>
        )}
        <Box display="flex" gap={2}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Show</InputLabel>
            <Select value={show} label="Show" onChange={(e) => setShow(e.target.value as string)}>
              <MenuItem value=""><em>All shows</em></MenuItem>
              {(shows?.shows || []).map((s: any) => (
                <MenuItem key={s.show_name} value={s.show_name}>
                  {s.show_name} ({fmt(s.total_downloads)})
                </MenuItem>
              ))}
              {/* Mall Brats has zero Flightpath rows (SES emails only cover SL),
                  so it won't appear in shows.shows. Inject manually so users
                  can still pick it — selecting it transparently switches the
                  data source to MUM. */}
              {!(shows?.shows || []).some((s: any) => s.show_name === 'Mall Brats') && (
                <MenuItem value="Mall Brats">
                  Mall Brats <Box component="span" sx={{ opacity: 0.6, ml: 0.5 }}>(via MUM)</Box>
                </MenuItem>
              )}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Window</InputLabel>
            <Select value={days} label="Window" onChange={(e) => setDays(Number(e.target.value))}>
              {DAY_OPTIONS.map((d) => <MenuItem key={d} value={d}>Last {d}d</MenuItem>)}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* KPI row */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, md: 3 }}>
          <Panel label="DOWNLOADS">
            {summaryLoading ? <Skeleton variant="text" width="80%" /> : (
              <Typography variant="h4" fontWeight={700}>{fmt(summary?.downloads)}</Typography>
            )}
            <Box mt={1}>{pctChip(summary?.downloads_change_pct)}</Box>
          </Panel>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Panel label="AD REVENUE">
            {summaryLoading ? <Skeleton variant="text" width="80%" /> : (
              <Typography variant="h4" fontWeight={700}>{usd(summary?.ad_revenue)}</Typography>
            )}
            <Box mt={1}>{pctChip(summary?.revenue_change_pct)}</Box>
          </Panel>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Panel label="IMPRESSIONS">
            {summaryLoading ? <Skeleton variant="text" width="80%" /> : (
              <Typography variant="h4" fontWeight={700}>{fmt(summary?.impressions)}</Typography>
            )}
            <Typography variant="caption" color="textSecondary">
              {summary?.active_episodes ?? 0} episodes active
            </Typography>
          </Panel>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Panel label="COVERAGE">
            {ingest ? (
              <>
                <Typography variant="h4" fontWeight={700}>{fmt(ingest.fact_rows)}</Typography>
                <Typography variant="caption" color="textSecondary">
                  {ingest.first_date} → {ingest.last_date}
                </Typography>
              </>
            ) : <Skeleton variant="text" width="60%" />}
          </Panel>
        </Grid>
      </Grid>

      {isMbFallback && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>Mall Brats data source = MUM</strong> (Megaphone's private CMS API).
          Flightpath SES emails don't cover the MB network so there's no per-country
          / per-DAI-slot / ad-revenue breakdown — the Geography and Ad Slots tabs
          will be empty for MB. Timeline + Top Episodes show real numbers.
        </Alert>
      )}

      <Paper sx={{ mb: 3 }}>
        <Tabs value={active} onChange={(_, v) => setActive(v)} variant="scrollable" scrollButtons="auto" sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab icon={<TrendingUp />} label="Timeline" />
          <Tab icon={<Podcasts />} label="Top Episodes" />
          <Tab icon={<Public />} label="Geography" />
          <Tab icon={<AdsClick />} label="Ad Slots" />
          <Tab icon={<Inbox />} label="Ingest Log" />
        </Tabs>
      </Paper>

      <TabPanel value={active} index={0}>
        <Panel label={`DAILY DOWNLOADS (last ${days}d${show ? ` — ${show}` : ''})`}>
          <Box sx={{ width: '100%', height: 340 }}>
            <ResponsiveContainer>
              <AreaChart data={timelinePoints}>
                <defs>
                  <linearGradient id="dl" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#00ff41" stopOpacity={0.7} />
                    <stop offset="95%" stopColor="#00ff41" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e3a1e" />
                <XAxis dataKey="date" stroke="#5aaa5a" />
                <YAxis stroke="#5aaa5a" />
                <Tooltip contentStyle={{ background: '#0a0a0a', border: '1px solid #1e3a1e', color: '#00ff41' }} />
                <Area type="monotone" dataKey="downloads" stroke="#00ff41" fill="url(#dl)" />
              </AreaChart>
            </ResponsiveContainer>
          </Box>
        </Panel>
        <Box mt={3}>
          <Panel label={`DAILY AD REVENUE (last ${days}d)`}>
            <Box sx={{ width: '100%', height: 280 }}>
              <ResponsiveContainer>
                <AreaChart data={timelinePoints}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e3a1e" />
                  <XAxis dataKey="date" stroke="#5aaa5a" />
                  <YAxis stroke="#5aaa5a" />
                  <Tooltip formatter={(v: any) => usd(Number(v))} contentStyle={{ background: '#0a0a0a', border: '1px solid #1e3a1e', color: '#ff6600' }} />
                  <Area type="monotone" dataKey="ad_revenue" stroke="#ff6600" fill="#ff6600" fillOpacity={0.3} />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          </Panel>
        </Box>
      </TabPanel>

      <TabPanel value={active} index={1}>
        <Panel label={`TOP EPISODES (last ${days}d)`}>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>SHOW</TableCell>
                  <TableCell>EPISODE</TableCell>
                  <TableCell align="right">DOWNLOADS</TableCell>
                  <TableCell align="right">AD REVENUE</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(isMbFallback
                  ? (mumTop?.episodes || []).map((e: any) => ({
                      show_name: 'Mall Brats',
                      episode_name: e.title || e.episode_id,
                      downloads: e.delivery,
                      ad_revenue: null,
                    }))
                  : (top?.episodes || [])
                ).map((e: any, i: number) => (
                  <TableRow key={`${e.show_name}-${e.episode_name}-${i}`}>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell>{e.show_name}</TableCell>
                    <TableCell>{e.episode_name}</TableCell>
                    <TableCell align="right">{fmt(e.downloads)}</TableCell>
                    <TableCell align="right">
                      {e.ad_revenue === null
                        ? <span style={{ opacity: 0.4 }}>n/a</span>
                        : usd(e.ad_revenue)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Panel>
      </TabPanel>

      <TabPanel value={active} index={2}>
        <Panel label={`DOWNLOADS BY COUNTRY (last ${days}d)`}>
          <Box sx={{ width: '100%', height: 380 }}>
            <ResponsiveContainer>
              <BarChart data={geo?.countries || []} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#1e3a1e" />
                <XAxis type="number" stroke="#5aaa5a" />
                <YAxis dataKey="country" type="category" width={60} stroke="#5aaa5a" />
                <Tooltip contentStyle={{ background: '#0a0a0a', border: '1px solid #1e3a1e', color: '#00ff41' }} />
                <Bar dataKey="downloads" fill="#00ff41" />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Panel>
      </TabPanel>

      <TabPanel value={active} index={3}>
        <Panel label={`DOWNLOADS BY SLOT (last ${days}d)`}>
          <Box sx={{ width: '100%', height: 320 }}>
            <ResponsiveContainer>
              <BarChart data={slotPoints}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e3a1e" />
                <XAxis dataKey="slot" stroke="#5aaa5a" />
                <YAxis stroke="#5aaa5a" />
                <Tooltip contentStyle={{ background: '#0a0a0a', border: '1px solid #1e3a1e', color: '#00ff41' }} />
                <Legend />
                <Bar dataKey="downloads" fill="#00ff41" />
                <Bar dataKey="ad_revenue" fill="#ff6600" />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Panel>
      </TabPanel>

      <TabPanel value={active} index={4}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Panel label="INGEST SUMMARY">
              {ingest ? (
                <Box sx={{ fontFamily: "'VT323', monospace", fontSize: '18px', lineHeight: 1.6 }}>
                  <div>files total:   <b>{fmt(ingest.files_total)}</b></div>
                  <div>files success: <b>{fmt(ingest.files_success)}</b></div>
                  <div>files failed:  <b style={{ color: ingest.files_failed > 0 ? '#ff3344' : undefined }}>{fmt(ingest.files_failed)}</b></div>
                  <div>fact rows:     <b>{fmt(ingest.fact_rows)}</b></div>
                  <div>date span:     <b>{ingest.first_date} → {ingest.last_date}</b></div>
                  <div>last load at:  <b>{ingest.last_load_at}</b></div>
                </Box>
              ) : <Skeleton variant="rectangular" height={160} />}
            </Panel>
          </Grid>
          <Grid size={{ xs: 12, md: 8 }}>
            <Panel label="RECENT FILES">
              <Box sx={{ overflowX: 'auto', maxHeight: 460 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>STATUS</TableCell>
                      <TableCell>KEY</TableCell>
                      <TableCell align="right">ROWS</TableCell>
                      <TableCell>LOADED</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(ingest?.recent_files || []).map((f: any) => (
                      <TableRow key={f.s3_key}>
                        <TableCell>
                          <Chip size="small" label={f.status}
                            sx={{
                              color: f.status === 'success' ? '#00ff41' : '#ff3344',
                              border: '1px solid',
                              borderColor: f.status === 'success' ? '#00ff41' : '#ff3344',
                              background: 'transparent',
                              fontFamily: "'VT323', monospace",
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '13px' }}>{f.s3_key}</TableCell>
                        <TableCell align="right">{fmt(f.row_count)}</TableCell>
                        <TableCell>{f.loaded_at}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Panel>
          </Grid>
        </Grid>
      </TabPanel>
    </>
  );

  if (embedded) return body;
  return <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>{body}</Container>;
};

export default FlightpathDashboard;
